import { Box, Button, CircularProgress } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormikProps } from "formik";
import {
  MacroAdjustmentCombinations,
  MacroAdjustmentStep,
  MacroAdjustmentWizardFormikValues,
} from "features/macro-tool/components/MacroAdjustmentWizard/MacroAdjustmentTypes";
import {
  CdeEnergyMixEfEntityDto,
  CdeFleetBreakdownEntityDto,
  IngredientEfEntityDto,
  IngredientWeightEntity,
  LogisticsDistanceEntityWithCountryDto,
  LogisticsVehicleMixEfEntityDto,
  ManufacturingEurEntityWithCountryDto,
  ManufacturingSourceMixEfEntityDto,
  PackagingEfEntityDto,
  PackagingNetLossRateEntityDto,
  PackagingRecoveryRateEntityWithCountryDto,
  PackagingRecycledContentEntityDto,
  SalesMixCategoryMixEntityDto,
  SalesMixDlzShareEntityDto,
  SalesMixPackMixEntityDto,
  SalesMixSalesGrowthEntityDto,
} from "orval/generated/models";
import { useGetAllScenariosMacroScenarioIdAdjustmentsGet } from "orval/generated/endpoint";
import { useSnackbar } from "components/common/Notification/showSnackbar";
import { useTranslation } from "react-i18next";
import { useModal } from "components/common/Modal";
import ConfirmationModal from "components/common/ConfirmationModal/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import { MacroToolRoutesConfig } from "features/macro-tool/navigation";
import {
  CheckConflictingAdjustment,
  CheckConflictingEfAdjustment,
} from "../HelperFunctions";

export type MacroAdjustmentsWizardFooterProps = {
  handleLeftButtonClick: () => void;
  steps: (
    formikProps: FormikProps<MacroAdjustmentWizardFormikValues>,
  ) => MacroAdjustmentStep[];
  formikProps: FormikProps<MacroAdjustmentWizardFormikValues>;
  activeStep: number;
  handleRightButtonClick: (
    formikProps: FormikProps<MacroAdjustmentWizardFormikValues>,
  ) => Promise<void>;
  adjustmentCombination: MacroAdjustmentCombinations | null;
  validationErrors: Record<number, string | undefined>;
  scenarioId: number;
  adjustmentId: number | undefined;
};

const MacroAdjustmentsWizardFooter = ({
  handleLeftButtonClick,
  steps,
  formikProps,
  activeStep,
  handleRightButtonClick,
  adjustmentCombination,
  validationErrors,
  scenarioId,
  adjustmentId,
}: MacroAdjustmentsWizardFooterProps) => {
  const { data: adjustments, isError: errorFetchingAdjustments } =
    useGetAllScenariosMacroScenarioIdAdjustmentsGet(scenarioId);

  const showSnackbar = useSnackbar();
  const { t } = useTranslation(["macro"]);
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();

  const [rightButtonDisabled, setRightButtonDisabled] = useState(true);
  const { values } = formikProps;
  const [isPending, setIsPending] = useState(false);

  const isConflictingEfAdjustment = useMemo<boolean>(() => {
    if (
      adjustments &&
      values.selectedPillar &&
      values.selectedInputArea &&
      values.payload.filters.countries[0] &&
      activeStep === 1
    ) {
      return CheckConflictingEfAdjustment(
        adjustmentCombination as MacroAdjustmentCombinations,
        adjustments,
        values.selectedPillar,
        values.selectedInputArea,
        values.payload.filters.countries[0] as string,
        adjustmentId,
      );
    } else {
      return false;
    }
  }, [
    activeStep,
    adjustmentCombination,
    adjustmentId,
    adjustments,
    values.payload.filters.countries,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  const isConflictingAdjustment = useMemo<boolean>(() => {
    if (
      adjustments &&
      values.selectedPillar &&
      values.selectedInputArea &&
      activeStep === 0
    ) {
      return CheckConflictingAdjustment(
        adjustmentCombination as MacroAdjustmentCombinations,
        adjustments,
        values.selectedPillar,
        values.selectedInputArea,
        adjustmentId,
      );
    } else {
      return false;
    }
  }, [
    activeStep,
    adjustmentCombination,
    adjustmentId,
    adjustments,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  const rightButtonStepOneDisabledValue = useMemo<boolean>(() => {
    const selectionsCheck =
      adjustmentCombination === null ||
      values.selectedPillar === null ||
      values.selectedInputArea === null ||
      values.selectedAdjustmentType === null ||
      values.payload.name === "" ||
      values.payload.name === null ||
      values.payload.name.length < 3;

    if (isConflictingAdjustment) {
      return true;
    }

    return selectionsCheck;
  }, [
    adjustmentCombination,
    isConflictingAdjustment,
    values.payload.name,
    values.selectedAdjustmentType,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  const rightButtonStepThreeDisabledValue = useMemo<boolean>(() => {
    if (!values.payload.entities.length) return true;

    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors: {
        const accordionsMap: { [key: number]: PackagingEfEntityDto[] } = {};

        (values.payload.entities as PackagingEfEntityDto[]).forEach(
          (e: PackagingEfEntityDto) => {
            if (accordionsMap[e.part_material_id])
              accordionsMap[e.part_material_id].push(e);
            else accordionsMap[e.part_material_id] = [e];
          },
        );

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordionsValidation.push(
            accordion.filter(
              (entity) =>
                entity.supply_share !== 0 && entity.supply_share !== undefined,
            ).length < 1,
          ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (values.payload.entities as PackagingEfEntityDto[]).filter(
            (entity) =>
              entity.supply_share === null ||
              entity.recycled_ef === null ||
              entity.virgin_ef === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
      }

      case MacroAdjustmentCombinations.Ingredients_IngredientsEmissionFactors: {
        const accordionsMap: { [key: number]: IngredientEfEntityDto[] } = {};

        (values.payload.entities as IngredientEfEntityDto[]).forEach(
          (e: IngredientEfEntityDto) => {
            if (accordionsMap[e.ingredient_id])
              accordionsMap[e.ingredient_id].push(e);
            else accordionsMap[e.ingredient_id] = [e];
          },
        );

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordionsValidation.push(
            accordion.filter(
              (entity) =>
                entity.supply_share !== 0 && entity.supply_share !== undefined,
            ).length < 1,
          ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (values.payload.entities as IngredientEfEntityDto[]).filter(
            (entity) => entity.supply_share === null || entity.ef === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
      }

      case MacroAdjustmentCombinations.Manufacturing_ManufacturingSourceMixAndEmissionFactors: {
        const accordionsMap: {
          [key: number]: ManufacturingSourceMixEfEntityDto[];
        } = {};

        (
          values.payload.entities as ManufacturingSourceMixEfEntityDto[]
        ).forEach((e: ManufacturingSourceMixEfEntityDto) => {
          if (accordionsMap[e.energy_type_id])
            accordionsMap[e.energy_type_id].push(e);
          else accordionsMap[e.energy_type_id] = [e];
        });

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordionsValidation.push(
            accordion.filter(
              (entity) =>
                entity.supply_share !== 0 && entity.supply_share !== undefined,
            ).length < 1,
          ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (
            values.payload.entities as ManufacturingSourceMixEfEntityDto[]
          ).filter(
            (entity) => entity.supply_share === null || entity.ef === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
      }

      case MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors: {
        const accordionsMap: {
          [key: string]: CdeEnergyMixEfEntityDto[];
        } = {};

        (values.payload.entities as CdeEnergyMixEfEntityDto[]).forEach(
          (e: CdeEnergyMixEfEntityDto) => {
            if (accordionsMap[e.country]) accordionsMap[e.country].push(e);
            else accordionsMap[e.country] = [e];
          },
        );

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordionsValidation.push(
            accordion.filter(
              (entity) =>
                entity.supply_share !== 0 && entity.supply_share !== undefined,
            ).length < 1,
          ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (values.payload.entities as CdeEnergyMixEfEntityDto[]).filter(
            (entity) => entity.supply_share === null || entity.ef === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
      }

      case MacroAdjustmentCombinations.Logistics_LogisticsRoadVehicleMixAndEmissionFactors:
      case MacroAdjustmentCombinations.Logistics_LogisticsOtherVehicleMixAndEmissionFactors:
        const accordionsMap: {
          [key: number]: LogisticsVehicleMixEfEntityDto[];
        } = {};

        (values.payload.entities as LogisticsVehicleMixEfEntityDto[]).forEach(
          (e: LogisticsVehicleMixEfEntityDto) => {
            if (accordionsMap[e.transport_mode_id])
              accordionsMap[e.transport_mode_id].push(e);
            else accordionsMap[e.transport_mode_id] = [e];
          },
        );

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordionsValidation.push(
            accordion.filter(
              (entity) =>
                entity.usage_share !== 0 && entity.usage_share !== undefined,
            ).length < 1,
          ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (values.payload.entities as LogisticsVehicleMixEfEntityDto[]).filter(
            (entity) => entity.usage_share === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.CDE_CDEFleetBreakdown:
        return (
          (values.payload.entities as CdeFleetBreakdownEntityDto[]).filter(
            (entity) =>
              entity.avg_energy_consumption === null ||
              entity.number_of_units === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.Packaging_NetLossRate:
        return (
          (values.payload.entities as PackagingNetLossRateEntityDto[]).filter(
            (entity) => entity.net_loss_rate <= 0,
          ).length > 0 ||
          (values.payload.entities as PackagingNetLossRateEntityDto[]).filter(
            (entity) => entity.net_loss_rate === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.Packaging_RecycledContent:
        return (
          (
            values.payload.entities as PackagingRecycledContentEntityDto[]
          ).filter((entity) => entity.recycled_content === null).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
        return (
          (
            values.payload
              .entities as PackagingRecoveryRateEntityWithCountryDto[]
          ).filter((entity) => entity.recovery_rate === null).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
        return (
          (
            values.payload.entities as ManufacturingEurEntityWithCountryDto[]
          ).filter((entity) => entity.eur === null).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        return (
          (
            values.payload.entities as LogisticsDistanceEntityWithCountryDto[]
          ).filter((entity) => entity.distance === null).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
        return (
          (values.payload.entities as SalesMixSalesGrowthEntityDto[]).filter(
            (entity) => entity.sales_growth === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
        return (
          (values.payload.entities as SalesMixCategoryMixEntityDto[]).filter(
            (entity) =>
              entity.category_mix !== 0 && entity.category_mix !== undefined,
          ).length < 1 ||
          (values.payload.entities as SalesMixCategoryMixEntityDto[]).filter(
            (entity) => entity.category_mix === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      case MacroAdjustmentCombinations.SalesMix_PackMix: {
        const accordionsMap: {
          [key: number]: SalesMixPackMixEntityDto[];
        } = {};

        (values.payload.entities as SalesMixPackMixEntityDto[]).forEach(
          (e: SalesMixPackMixEntityDto) => {
            if (accordionsMap[e.category_id])
              accordionsMap[e.category_id].push(e);
            else accordionsMap[e.category_id] = [e];
          },
        );

        let accordionsValidation: boolean[] = [];

        Object.values(accordionsMap).forEach((accordion) =>
          accordion.every((entity) => entity.pack_mix === undefined)
            ? false
            : accordionsValidation.push(
                accordion.filter(
                  (entity) =>
                    entity.pack_mix !== 0 && entity.pack_mix !== undefined,
                ).length < 1,
              ),
        );

        return (
          accordionsValidation.filter((check) => check).length > 0 ||
          (values.payload.entities as SalesMixPackMixEntityDto[]).filter(
            (entity) => entity.pack_mix === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
      }

      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        return (
          (values.payload.entities as SalesMixDlzShareEntityDto[]).filter(
            (entity) => entity.dlz_share === null,
          ).length > 0 ||
          (values.payload.entities as SalesMixDlzShareEntityDto[]).every(
            (entity) => entity.dlz_share === undefined,
          ) ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );

      default:
        return (
          (values.payload.entities as IngredientWeightEntity[]).filter(
            (entity) => entity.weight === null,
          ).length > 0 ||
          Object.values(validationErrors).some((error) => error !== undefined)
        );
    }
  }, [adjustmentCombination, validationErrors, values.payload.entities]);

  const rightButtonStepTwoDisabledValue = useMemo<boolean>(() => {
    let selectionsCheck = false;
    switch (adjustmentCombination) {
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
        selectionsCheck = values.selectedEntities.length === 0;
        break;
      case MacroAdjustmentCombinations.CDE_CDEFleetBreakdown:
      case MacroAdjustmentCombinations.Packaging_PackagingEmissionFactors:
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingSourceMixAndEmissionFactors:
      case MacroAdjustmentCombinations.Ingredients_IngredientsEmissionFactors:
      case MacroAdjustmentCombinations.Logistics_LogisticsOtherVehicleMixAndEmissionFactors:
      case MacroAdjustmentCombinations.Logistics_LogisticsRoadVehicleMixAndEmissionFactors:
        selectionsCheck =
          values.selectedEntities.length === 0 ||
          values.selectedCountries.length === 0;
        break;
      case MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors:
        selectionsCheck = values.selectedCountries.length === 0;
        break;
      case MacroAdjustmentCombinations.Ingredients_IngredientWeights_SpecificBrandAdjustment:
        selectionsCheck =
          values.selectedEntities.length === 0 ||
          values.payload.filters.brands?.length === 0;
        break;
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_MultiSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_PackagingWeights_SpecificSKUAdjustment:
      case MacroAdjustmentCombinations.Packaging_RecycledContent:
      case MacroAdjustmentCombinations.Packaging_NetLossRate:
      case MacroAdjustmentCombinations.Packaging_RecoveryRate:
      case MacroAdjustmentCombinations.Manufacturing_ManufacturingEur:
      case MacroAdjustmentCombinations.Logistics_DistanceTravelled:
        selectionsCheck =
          values.selectedProducts.length === 0 ||
          values.selectedEntities.length === 0;
        break;

      case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
        selectionsCheck = values.payload.filters.countries?.length === 0;
        break;

      case MacroAdjustmentCombinations.SalesMix_CategoryMix:
      case MacroAdjustmentCombinations.SalesMix_PackMix:
        selectionsCheck = values.payload.filters.countries?.length === 0;
        break;
      case MacroAdjustmentCombinations.SalesMix_DLZShare:
        selectionsCheck = values.payload.filters.countries?.length === 0;
        break;
      default:
        selectionsCheck = false;
        break;
    }

    if (isConflictingEfAdjustment) {
      return true;
    }

    return selectionsCheck;
  }, [
    adjustmentCombination,
    isConflictingEfAdjustment,
    values.payload.filters,
    values.selectedCountries.length,
    values.selectedEntities.length,
    values.selectedProducts.length,
  ]);

  const updateRightButtonDisabled = useCallback(() => {
    switch (activeStep) {
      // STEP ONE PAGE:
      case 0:
        setRightButtonDisabled(rightButtonStepOneDisabledValue);
        break;
      // STEP TWO PAGE - ADDITIONAL SWITCH FOR STEP TWO:
      case 1:
        setRightButtonDisabled(rightButtonStepTwoDisabledValue);
        break;
      // STEP THREE PAGE:
      case 2:
        setRightButtonDisabled(rightButtonStepThreeDisabledValue);
        break;
      default:
        setRightButtonDisabled(false);
    }
  }, [
    activeStep,
    rightButtonStepOneDisabledValue,
    rightButtonStepThreeDisabledValue,
    rightButtonStepTwoDisabledValue,
  ]);

  useEffect(() => {
    updateRightButtonDisabled();
  }, [activeStep, formikProps, updateRightButtonDisabled]);

  useEffect(() => {
    if (
      (isConflictingEfAdjustment || isConflictingAdjustment) &&
      (values.selectedPillar ||
        values.selectedInputArea ||
        values.selectedCountries) //This is to make the effect run upon changing those values as well so a warning is displayed for each conflicting adjustment
    ) {
      openModal(
        <ConfirmationModal
          title={t(
            "macro:adjustmentsPage.wizard.stepTwo.conflictingAdjustmentModal.title",
          )}
          description={t(
            `macro:adjustmentsPage.wizard.stepTwo.conflictingAdjustmentModal.${isConflictingEfAdjustment ? "conflictingEfAdjustmentModalDescription" : "conflictingAdjustmentModalDescription"}`,
          )}
          actionTitle={t("macro:adjustmentsPage.wizard.backToAdjustmentsPage")}
          cancelTitle={t("common:actions.review")}
          confirmAction={() => {
            closeModal();
            navigate(
              MacroToolRoutesConfig.adjustmentsPage.replace(
                ":id",
                `${scenarioId}`,
              ),
            );
          }}
          cancelAction={() => {
            closeModal();
          }}
        />,
      );
    }
  }, [
    closeModal,
    isConflictingAdjustment,
    isConflictingEfAdjustment,
    navigate,
    openModal,
    scenarioId,
    t,
    values.selectedCountries,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  useEffect(() => {
    if (errorFetchingAdjustments) {
      showSnackbar(t("adjustmentsPage.failedToFetchAdjustments"), "error");
    }
  }, [errorFetchingAdjustments, showSnackbar, t]);

  return (
    <Box display={"flex"} justifyContent={"space-between"} mb={3}>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => handleLeftButtonClick()}
        disabled={isPending}
        sx={{ fontWeight: 400 }}
      >
        {steps(formikProps)[activeStep].leftBtnText}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={async () => {
          activeStep === 2 ? setIsPending(true) : setIsPending(false);

          try {
            await handleRightButtonClick(formikProps);
          } catch (e) {
            console.log(e);
          } finally {
            setIsPending(false);
          }
        }}
        disabled={rightButtonDisabled || isPending}
      >
        {steps(formikProps)[activeStep].rightBtnText}
        {isPending ? (
          <CircularProgress size={24} sx={{ color: "white", ml: 1 }} />
        ) : null}
      </Button>
    </Box>
  );
};

export default MacroAdjustmentsWizardFooter;
